import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Eye, Loader2, X } from 'lucide-react';
import {
	Button,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui';
import { WhatsAppMessagePreview } from '@/components/whatsapp/WhatsAppMessagePreview';
import {
	buildDefaultSessionWhatsAppMessage,
	SESSION_WHATSAPP_STATUS_LABEL,
	SESSION_WHATSAPP_STATUSES,
	SESSION_WHATSAPP_TYPE_LABEL,
	SESSION_WHATSAPP_TYPES,
} from '@/utils/sessionWhatsAppDisplay';
import { formatProfessionalFullName } from '@/utils';
import type { resolveSessionWhatsAppContext } from '@/utils/resolveSessionWhatsAppContext';

const toDateTimeLocalValue = (iso?: string): string => {
	const date = iso ? new Date(iso) : new Date();
	if (Number.isNaN(date.getTime())) return '';
	const offset = date.getTimezoneOffset();
	const local = new Date(date.getTime() - offset * 60_000);
	return local.toISOString().slice(0, 16);
};

type SessionWhatsAppFormPayload = {
	type: SessionWhatsApp.SessionWhatsAppNotificationType;
	scheduledFor: string;
	phone: string;
	body: string;
	status?: SessionWhatsApp.SessionWhatsAppNotificationStatus;
	statusDescription?: string;
};

type SessionWhatsAppCreateModalProps = {
	isOpen: boolean;
	onClose: () => void;
	session: Sessions.Session;
	sessionContext: ReturnType<typeof resolveSessionWhatsAppContext>;
	isSubmitting: boolean;
	notification?: SessionWhatsApp.SessionWhatsAppNotification | null;
	onSubmit: (payload: SessionWhatsAppFormPayload) => void;
};

const SessionWhatsAppCreateModal = ({
	isOpen,
	onClose,
	session,
	sessionContext,
	isSubmitting,
	notification = null,
	onSubmit,
}: SessionWhatsAppCreateModalProps) => {
	const isEditMode = notification != null;

	const [formType, setFormType] =
		useState<SessionWhatsApp.SessionWhatsAppNotificationType>(
			'SESSION_SCHEDULED',
		);
	const [formScheduledFor, setFormScheduledFor] = useState('');
	const [formPhone, setFormPhone] = useState('');
	const [formBody, setFormBody] = useState('');
	const [formStatus, setFormStatus] =
		useState<SessionWhatsApp.SessionWhatsAppNotificationStatus>('QUEUED');
	const [formStatusDescription, setFormStatusDescription] = useState('');
	const [isPreviewOpen, setIsPreviewOpen] = useState(false);

	const professionalName = session.professional
		? formatProfessionalFullName(session.professional)
		: undefined;

	useEffect(() => {
		if (!isOpen) return;

		if (notification) {
			setFormType(notification.type);
			setFormScheduledFor(
				toDateTimeLocalValue(notification.scheduled_for),
			);
			setFormPhone(notification.phone_snapshot.replace(/\D/g, ''));
			setFormBody(notification.rendered_body);
			setFormStatus(notification.status);
			setFormStatusDescription(notification.status_description ?? '');
		} else {
			const phoneDigits = (sessionContext.phone ?? '').replace(/\D/g, '');
			setFormType('SESSION_SCHEDULED');
			setFormScheduledFor(toDateTimeLocalValue(session.selected_date));
			setFormPhone(phoneDigits);
			setFormBody(
				buildDefaultSessionWhatsAppMessage('SESSION_SCHEDULED', {
					patientName: sessionContext.patientName,
					sessionDateIso: session.selected_date,
					professionalName,
				}),
			);
			setFormStatus('QUEUED');
			setFormStatusDescription('');
		}

		setIsPreviewOpen(false);
	}, [
		isOpen,
		notification,
		session.id,
		session.selected_date,
		sessionContext.patientName,
		professionalName,
	]);

	useEffect(() => {
		if (!isOpen || notification) return;

		const phoneDigits = (sessionContext.phone ?? '').replace(/\D/g, '');
		if (!phoneDigits) return;

		setFormPhone((current) => current || phoneDigits);
	}, [isOpen, notification, sessionContext.phone]);

	useEffect(() => {
		if (!isOpen) return;

		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape' && !isSubmitting && !isPreviewOpen)
				onClose();
		};

		window.addEventListener('keydown', onKeyDown);
		return () => window.removeEventListener('keydown', onKeyDown);
	}, [isOpen, isSubmitting, isPreviewOpen, onClose]);

	if (!isOpen) return null;

	const handleSubmit = () => {
		onSubmit({
			type: formType,
			scheduledFor: formScheduledFor,
			phone: formPhone,
			body: formBody,
			...(isEditMode
				? {
						status: formStatus,
						statusDescription: formStatusDescription,
				  }
				: {}),
		});
	};

	return createPortal(
		<>
			<div className="fixed inset-0 z-[10001] flex items-center justify-center p-4">
				<div
					className="absolute inset-0 bg-stone-900/70 backdrop-blur-[2px]"
					onClick={isSubmitting ? undefined : onClose}
					aria-hidden
				/>
				<div
					className="animate-in zoom-in-95 relative flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-[1.75rem] bg-white shadow-2xl duration-200"
					role="dialog"
					aria-modal="true"
					aria-labelledby="session-whatsapp-form-title"
				>
					<div className="flex shrink-0 items-center justify-between border-b border-stone-100 px-6 py-5">
						<h3
							id="session-whatsapp-form-title"
							className="text-lg font-bold text-stone-900"
						>
							{isEditMode
								? 'Editar notificação'
								: 'Nova notificação'}
						</h3>
						<button
							type="button"
							onClick={onClose}
							disabled={isSubmitting}
							className="text-stone-400 transition hover:text-stone-600 disabled:opacity-50"
							aria-label="Fechar"
						>
							<X size={20} />
						</button>
					</div>

					<div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-5">
						<div>
							<label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-stone-500">
								Tipo
							</label>
							<Select
								value={formType}
								onValueChange={(value) => {
									const nextType =
										value as SessionWhatsApp.SessionWhatsAppNotificationType;
									setFormType(nextType);
									if (!isEditMode) {
										setFormBody(
											buildDefaultSessionWhatsAppMessage(
												nextType,
												{
													patientName:
														sessionContext.patientName,
													sessionDateIso:
														session.selected_date,
													professionalName,
												},
											),
										);
									}
								}}
							>
								<SelectTrigger className="h-10 w-full border-stone-200 bg-white text-left text-sm">
									<SelectValue>
										{SESSION_WHATSAPP_TYPE_LABEL[formType]}
									</SelectValue>
								</SelectTrigger>
								<SelectContent>
									{SESSION_WHATSAPP_TYPES.map((type) => (
										<SelectItem key={type} value={type}>
											{SESSION_WHATSAPP_TYPE_LABEL[type]}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<div>
							<label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-stone-500">
								Agendada para
							</label>
							<input
								type="datetime-local"
								value={formScheduledFor}
								onChange={(event) =>
									setFormScheduledFor(event.target.value)
								}
								className="h-10 w-full rounded-md border border-stone-200 bg-white px-3 text-sm text-stone-700 outline-none focus:border-stone-400"
							/>
						</div>
						<div>
							<label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-stone-500">
								Telefone (11 dígitos)
							</label>
							<input
								type="text"
								value={formPhone}
								onChange={(event) =>
									setFormPhone(event.target.value)
								}
								inputMode="numeric"
								maxLength={11}
								className="h-10 w-full rounded-md border border-stone-200 bg-white px-3 text-sm text-stone-700 outline-none focus:border-stone-400"
							/>
						</div>
						{isEditMode ? (
							<>
								<div>
									<label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-stone-500">
										Status
									</label>
									<Select
										value={formStatus}
										onValueChange={(value) =>
											setFormStatus(
												value as SessionWhatsApp.SessionWhatsAppNotificationStatus,
											)
										}
									>
										<SelectTrigger className="h-10 w-full border-stone-200 bg-white text-left text-sm">
											<SelectValue>
												{
													SESSION_WHATSAPP_STATUS_LABEL[
														formStatus
													]
												}
											</SelectValue>
										</SelectTrigger>
										<SelectContent>
											{SESSION_WHATSAPP_STATUSES.map(
												(status) => (
													<SelectItem
														key={status}
														value={status}
													>
														{
															SESSION_WHATSAPP_STATUS_LABEL[
																status
															]
														}
													</SelectItem>
												),
											)}
										</SelectContent>
									</Select>
								</div>
								<div>
									<label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-stone-500">
										Descrição do status
									</label>
									<textarea
										value={formStatusDescription}
										onChange={(event) =>
											setFormStatusDescription(
												event.target.value,
											)
										}
										rows={2}
										className="w-full resize-none rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm text-stone-700 outline-none focus:border-stone-400"
									/>
								</div>
							</>
						) : null}
						<div>
							<div className="mb-1 flex items-center justify-between gap-2">
								<label className="text-[10px] font-bold uppercase tracking-wider text-stone-500">
									Mensagem
								</label>
								<button
									type="button"
									onClick={() => setIsPreviewOpen(true)}
									disabled={!formBody.trim()}
									className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 underline-offset-2 transition hover:text-emerald-900 hover:underline disabled:cursor-not-allowed disabled:opacity-40"
								>
									<Eye size={12} />
									Pré-visualizar
								</button>
							</div>
							<textarea
								value={formBody}
								onChange={(event) =>
									setFormBody(event.target.value)
								}
								rows={6}
								className="w-full resize-y rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm text-stone-700 outline-none focus:border-stone-400"
							/>
						</div>
					</div>

					<div className="flex shrink-0 justify-end gap-2 border-t border-stone-100 px-6 py-4">
						<Button
							type="button"
							text="Cancelar"
							disabled={isSubmitting}
							className="rounded-lg border border-stone-200 bg-white px-4 py-2 text-sm font-semibold text-stone-600 hover:bg-stone-50 disabled:opacity-50"
							onClick={onClose}
						/>
						<Button
							type="button"
							text={
								isSubmitting
									? 'Salvando…'
									: isEditMode
									  ? 'Salvar alterações'
									  : 'Inserir notificação'
							}
							disabled={isSubmitting}
							className="rounded-lg bg-stone-900 px-4 py-2 text-sm font-semibold text-white hover:bg-stone-800 disabled:opacity-50"
							startIcon={
								isSubmitting ? (
									<Loader2 className="size-4 animate-spin" />
								) : undefined
							}
							onClick={handleSubmit}
						/>
					</div>
				</div>
			</div>

			<WhatsAppMessagePreview
				isOpen={isPreviewOpen}
				onClose={() => setIsPreviewOpen(false)}
				message={formBody}
				patientName={sessionContext.patientName}
				patientPhone={formPhone || sessionContext.phone}
			/>
		</>,
		document.body,
	);
};

export { SessionWhatsAppCreateModal };
export type { SessionWhatsAppFormPayload };
